/**
 * What CancelArticle({force: true}) gives back for one article, so the cancel
 * button and the failed-scrape dialog promise only that (#30943).
 * Keep in sync with refundableCredit and applyArticleCancel in
 * functions/src/Subscription/articleCredit.ts: a monthly charge always comes
 * back, extra and free only after a failed scrape. An article without
 * creditUsed (not charged yet, or created before creditUsed existed) gets
 * nothing back.
 */
export type ArticleCreditUsed = 'extra' | 'free' | 'monthly';

/** creditUsed as ArticleOnCreateObserver wrote it; anything else is null. */
export function readCreditUsed(value: unknown): ArticleCreditUsed | null {
    return value === 'extra' || value === 'free' || value === 'monthly' ? value : null;
}

/** The balance CancelArticle with force returns, or null when it returns none. */
export function cancelArticleRefund(creditUsed: unknown, scrapeFailed: boolean): ArticleCreditUsed | null {
    const credit = readCreditUsed(creditUsed);
    if (credit === 'monthly') {
        return credit;
    }
    return scrapeFailed ? credit : null;
}

/**
 * Label of "Cancel this article" under a keyword article with few inspirations.
 * A keyword article never has a failed scrape, so only a monthly slot comes back.
 */
export function cancelArticleButtonKey(creditUsed: unknown): string {
    if (cancelArticleRefund(creditUsed, false) === 'monthly') {
        return 'keywords.deleteArticleAndRefundCredit';
    }
    const credit = readCreditUsed(creditUsed);
    if (credit === 'extra') {
        return 'keywords.deleteArticleKeepExtraCredit';
    }
    if (credit === 'free') {
        return 'keywords.deleteArticleKeepFreeAnalysis';
    }
    return 'keywords.deleteArticleNoRefund';
}

/** Description of "Delete article" in the failed-scrape dialog: the scrape failed. */
export function scrapeFailDeleteDescriptionKey(creditUsed: unknown): string {
    const refund = cancelArticleRefund(creditUsed, true);
    if (refund === 'monthly') {
        return 'article.analyze.scrapeFail.deleteArticle.descriptionMonthly';
    }
    if (refund === 'extra') {
        return 'article.analyze.scrapeFail.deleteArticle.descriptionExtra';
    }
    if (refund === 'free') {
        return 'article.analyze.scrapeFail.deleteArticle.descriptionFree';
    }
    return 'article.analyze.scrapeFail.deleteArticle.descriptionNoRefund';
}
